import { TFunction } from 'next-i18next';
import { z } from 'zod';
import { MainPalette } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/config/palette-config';
import { createPartnerNameSchema } from '../../validation/partner-names/schema';

const TWebsitePaletteSchema = z.enum(Object.values(MainPalette) as [MainPalette, ...MainPalette[]]);

export const isDateNotPast = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date >= today;
};

export const isValidDateFormat = (dateString: string) => /^\d{4}-\d{2}-\d{2}$/.test(dateString);

export const fieldRules = {
  name: (t: TFunction) =>
    createPartnerNameSchema({
      allowEmpty: false,
      messages: {
        required: t('weddingWebsite:validation.name.required'),
      },
    }),
  partnerName: (t: TFunction) =>
    createPartnerNameSchema({
      allowEmpty: false,
      messages: {
        required: t('weddingWebsite:validation.partnerName.required'),
      },
    }),
  weddingDate: z
    .string()
    .optional()
    .refine((val) => !val || isValidDateFormat(val)),
};

export const getNamesFormValidationSchema = (t: TFunction) =>
  z.object({
    name: fieldRules.name(t),
    partnerName: fieldRules.partnerName(t),
  });

export const getPinFormValidationSchema = (t: TFunction) =>
  z.object({
    pin: z.string().min(1, t('weddingWebsite:validation.pin.required')),
  });

export const getOnboardingFormValidationSchema = (t: TFunction) =>
  z
    .object({
      name: fieldRules.name(t),
      partnerName: fieldRules.partnerName(t),
      weddingDate: fieldRules.weddingDate,
      venueId: z.string().optional(),
      palette: TWebsitePaletteSchema,
      photo: z.union([z.object({}).passthrough(), z.null(), z.literal('STATE_UPLOADING')]),
      weddingDateUndecided: z.boolean(),
      venueIdUndecided: z.boolean(),
    })
    .refine(
      (data) => {
        if (data.weddingDateUndecided) {
          return true;
        }
        return !!data.weddingDate;
      },
      {
        message: t('weddingWebsite:validation.weddingDate.requiredUnlessUndecided'),
        path: ['weddingDate'],
      },
    )
    .refine(
      (data) => {
        if (data.venueIdUndecided || data.weddingDateUndecided) {
          return true;
        }
        return !!data.venueId;
      },
      {
        message: t('weddingWebsite:validation.venueId.requiredUnlessUndecided'),
        path: ['venueId'],
      },
    )
    .refine(
      (data) => {
        if (data.weddingDateUndecided || !data.weddingDate) {
          return true;
        }
        return isDateNotPast(data.weddingDate);
      },
      {
        message: t('weddingWebsite:validation.weddingDate.notInPast'),
        path: ['weddingDate'],
      },
    )
    .refine(
      (data) => {
        if (data.photo === 'STATE_UPLOADING') {
          return false;
        }
        return true;
      },
      {
        message: 'INTERNAL_UPLOADING_PHOTO',
        path: ['photo'],
      },
    );

export const getVenueDateFormValidationSchema = (t: TFunction) =>
  z
    .object({
      weddingDate: fieldRules.weddingDate,
      venueId: z.string().optional(),
    })
    .refine((data) => !data.venueId || data.weddingDate, {
      message: t('weddingWebsite:validation.venueId.weddingDateRequired'),
      path: ['weddingDate'],
    })
    .refine((data) => !data.weddingDate || isDateNotPast(data.weddingDate), {
      message: t('weddingWebsite:validation.weddingDate.notInPast'),
      path: ['weddingDate'],
    });

export const getRsvpDeadlineFormValidationSchema = (t: TFunction) =>
  z.object({
    deadline: z
      .string()
      .optional()
      .refine((val) => !val || isValidDateFormat(val), {
        message: t('weddingWebsite:rsvp.main.date.invalidFormat'),
      })
      .refine((val) => !val || isDateNotPast(val), {
        message: t('weddingWebsite:rsvp.main.date.error'),
      }),
  });
